import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Divider,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    Assessment as AssessmentIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    SportsEsports as SportsEsportsIcon,
    Palette as PaletteIcon,
    Person as PersonIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { getAllInkColors } from './InkColorIcon';

const DeckStatsModal = ({ open, onClose, deck }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const inkColors = getAllInkColors();

    if (!deck || !deck.stats) {
        return null;
    }

    const { stats } = deck;
    const overall = stats?.overall || { games: 0, wins: 0, losses: 0, winrate: 0, play: { games: 0, wins: 0, losses: 0, winrate: 0 }, draw: { games: 0, wins: 0, losses: 0, winrate: 0 } };
    const vsArchetypes = stats?.vsArchetypes || {};
    const vsColors = stats?.vsColors || {};
    const perPlayer = stats?.perPlayer || {};

    const getWinrateColor = (winrate) => {
        if (winrate >= 60) return '#10b981';
        if (winrate >= 50) return '#f59e0b';
        return '#ef4444';
    };

    const getInkColor = (color) => {
        const colorData = inkColors.find(c => c.value.toLowerCase() === (color || '').toLowerCase());
        return colorData?.color || '#666';
    };

    const formatColorName = (color) => {
        const colorData = inkColors.find(c => c.value.toLowerCase() === (color || '').toLowerCase());
        return colorData?.label || color;
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            fullScreen={isSmallMobile}
            sx={{
                '& .MuiDialog-paper': {
                    backgroundColor: 'rgba(17, 24, 39, 0.95)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(76, 29, 149, 0.3)',
                    borderRadius: isSmallMobile ? 0 : '16px'
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: 'white',
                borderBottom: '1px solid rgba(76, 29, 149, 0.3)',
                pb: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AssessmentIcon sx={{ color: '#7c3aed' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Statystyki talii: {deck.name}
                    </Typography>
                </Box>
                <Button
                    onClick={onClose}
                    sx={{
                        color: 'rgba(196, 181, 253, 0.7)',
                        minWidth: 'auto',
                        p: 1
                    }}
                >
                    <CloseIcon />
                </Button>
            </DialogTitle>

            <DialogContent sx={{ p: isSmallMobile ? 2 : 3 }}>
                {/* Ogólne statystyki */}
                <Card sx={{
                    background: 'rgba(17, 24, 39, 0.8)',
                    border: '1px solid rgba(76, 29, 149, 0.3)',
                    borderRadius: '12px',
                    mb: 3
                }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 2 }}>
                            Ogólne Statystyki
                        </Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={6} sm={3}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                                        {overall.games}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                        Łączne mecze
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="h4" sx={{ color: '#10b981', fontWeight: 700, mb: 1 }}>
                                        {overall.wins}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                        Wygrane
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="h4" sx={{ color: '#ef4444', fontWeight: 700, mb: 1 }}>
                                        {overall.losses}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                        Przegrane
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            color: getWinrateColor(overall.winrate),
                                            fontWeight: 700,
                                            mb: 1
                                        }}
                                    >
                                        {overall.winrate}%
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                        Winrate
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 2, borderColor: 'rgba(76, 29, 149, 0.3)' }} />

                        {/* Statystyki play vs draw */}
                        <Grid container spacing={3}>
                            <Grid item xs={6}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                                        Gdy zaczynam (Play)
                                    </Typography>
                                    <Typography variant="h5" sx={{ color: getWinrateColor(overall.play.winrate), fontWeight: 700, mb: 1 }}>
                                        {overall.play.winrate}%
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                        {overall.play.wins}W / {overall.play.losses}L ({overall.play.games} gier)
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                                        Gdy przeciwnik zaczyna (Draw)
                                    </Typography>
                                    <Typography variant="h5" sx={{ color: getWinrateColor(overall.draw.winrate), fontWeight: 700, mb: 1 }}>
                                        {overall.draw.winrate}%
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                        {overall.draw.wins}W / {overall.draw.losses}L ({overall.draw.games} gier)
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                <Grid container spacing={3}>
                    {/* Statystyki vs Archetypy */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{
                            background: 'rgba(17, 24, 39, 0.8)',
                            border: '1px solid rgba(76, 29, 149, 0.3)',
                            borderRadius: '12px',
                            height: '100%'
                        }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <SportsEsportsIcon sx={{ color: '#7c3aed', mr: 1 }} />
                                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                                        vs Archetypy Przeciwnika
                                    </Typography>
                                </Box>

                                {Object.keys(vsArchetypes).length === 0 ? (
                                    <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.7)', textAlign: 'center', py: 2 }}>
                                        Brak danych o meczach vs archetypy
                                    </Typography>
                                ) : (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600, fontSize: '0.8rem' }}>
                                                        Archetyp
                                                    </TableCell>
                                                    <TableCell sx={{ color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600, fontSize: '0.8rem' }}>
                                                        WR
                                                    </TableCell>
                                                    <TableCell sx={{ color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600, fontSize: '0.8rem' }}>
                                                        Gry
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {Object.entries(vsArchetypes).map(([archetypeId, archetypeStats]) => (
                                                    <TableRow key={archetypeId}>
                                                        <TableCell sx={{ color: 'white', fontSize: '0.8rem' }}>
                                                            {archetypeStats.name}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={`${archetypeStats.winrate}%`}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: getWinrateColor(archetypeStats.winrate),
                                                                    color: 'white',
                                                                    fontSize: '0.7rem',
                                                                    height: 20
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={{ color: 'white', fontSize: '0.8rem' }}>
                                                            {archetypeStats.games}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Statystyki vs Kolory */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{
                            background: 'rgba(17, 24, 39, 0.8)',
                            border: '1px solid rgba(76, 29, 149, 0.3)',
                            borderRadius: '12px',
                            height: '100%'
                        }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <PaletteIcon sx={{ color: '#3b82f6', mr: 1 }} />
                                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                                        vs Kolory Atramentu
                                    </Typography>
                                </Box>

                                {Object.keys(vsColors).length === 0 ? (
                                    <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.7)', textAlign: 'center', py: 2 }}>
                                        Brak danych o meczach vs kolory
                                    </Typography>
                                ) : (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600, fontSize: '0.8rem' }}>
                                                        Kolory
                                                    </TableCell>
                                                    <TableCell sx={{ color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600, fontSize: '0.8rem' }}>
                                                        WR
                                                    </TableCell>
                                                    <TableCell sx={{ color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600, fontSize: '0.8rem' }}>
                                                        Gry
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {Object.entries(vsColors).map(([colorCombo, colorStats]) => (
                                                    <TableRow key={colorCombo}>
                                                        <TableCell sx={{ color: 'white', fontSize: '0.8rem' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                {colorStats.colors.map((color, index) => (
                                                                    <Box
                                                                        key={index}
                                                                        sx={{
                                                                            width: 16,
                                                                            height: 16,
                                                                            borderRadius: '50%',
                                                                            backgroundColor: getInkColor(color),
                                                                            border: `1px solid ${getInkColor(color)}`
                                                                        }}
                                                                    />
                                                                ))}
                                                                <Typography variant="caption" sx={{ ml: 0.5 }}>
                                                                    {colorStats.colors.map(formatColorName).join('/')}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={`${colorStats.winrate}%`}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: getWinrateColor(colorStats.winrate),
                                                                    color: 'white',
                                                                    fontSize: '0.7rem',
                                                                    height: 20
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={{ color: 'white', fontSize: '0.8rem' }}>
                                                            {colorStats.games}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Statystyki per gracz */}
                {Object.keys(perPlayer).length > 0 && (
                    <Card sx={{
                        background: 'rgba(17, 24, 39, 0.8)',
                        border: '1px solid rgba(76, 29, 149, 0.3)',
                        borderRadius: '12px',
                        mt: 3
                    }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <PersonIcon sx={{ color: '#10b981', mr: 1 }} />
                                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                                    Statystyki per Gracz
                                </Typography>
                            </Box>

                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600, fontSize: '0.8rem' }}>
                                                Gracz
                                            </TableCell>
                                            <TableCell sx={{ color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600, fontSize: '0.8rem' }}>
                                                WR
                                            </TableCell>
                                            <TableCell sx={{ color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600, fontSize: '0.8rem' }}>
                                                Wygrane
                                            </TableCell>
                                            <TableCell sx={{ color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600, fontSize: '0.8rem' }}>
                                                Przegrane
                                            </TableCell>
                                            <TableCell sx={{ color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600, fontSize: '0.8rem' }}>
                                                Gry
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {Object.entries(perPlayer).map(([playerId, playerStats]) => (
                                            <TableRow key={playerId}>
                                                <TableCell sx={{ color: 'white', fontSize: '0.8rem' }}>
                                                    {playerStats.name}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={`${playerStats.winrate}%`}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: getWinrateColor(playerStats.winrate),
                                                            color: 'white',
                                                            fontSize: '0.7rem',
                                                            height: 20
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ color: '#10b981', fontSize: '0.8rem', fontWeight: 600 }}>
                                                    {playerStats.wins}
                                                </TableCell>
                                                <TableCell sx={{ color: '#ef4444', fontSize: '0.8rem', fontWeight: 600 }}>
                                                    {playerStats.losses}
                                                </TableCell>
                                                <TableCell sx={{ color: 'white', fontSize: '0.8rem' }}>
                                                    {playerStats.games}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(76, 29, 149, 0.3)' }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                >
                    Zamknij
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeckStatsModal;
